import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Select,
  NumberInput,
  NumberInputField,
  Checkbox,
  CheckboxGroup,
  useColorModeValue,
  useDisclosure,
  Divider,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';

import type { AccidentBenefits } from 'api/quote-marketplace';
import { apiCreateQuoteConsent } from 'api/quote-marketplace';
import {
  ACCIDENT_BENEFIT_DEFINITIONS,
  createEmptyAccidentBenefits,
  type AccidentBenefitDefinition,
  type AccidentBenefitEnabledKey,
  type AccidentBenefitLimitKey,
} from '../constants/accidentBenefitsOptions';
import { COVERAGE_ATTESTATION_TEXT } from '../constants/allCoveragesOptions';
import { CONSENT_TYPE_ACCIDENT_BENEFITS_DECLINE } from '../constants';
import { useAnswerCodes, type AnswerCodeField } from '../hooks/useAnswerCodes';
import DeclineAttestationModal from './DeclineAttestationModal';

interface AccidentBenefitsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (benefits: AccidentBenefits, confirmed: boolean) => void;
  initialBenefits?: AccidentBenefits;
  initialConfirmed?: boolean;
  vehicleLabel: string;
  quoteId: number;
  vehicleVin: string;
  vehicleId: number;
}

const AccidentBenefitsModal = ({
  isOpen,
  onClose,
  onSave,
  initialBenefits,
  initialConfirmed,
  vehicleLabel,
  quoteId,
  vehicleVin,
  vehicleId,
}: AccidentBenefitsModalProps) => {
  const descriptionColor = useColorModeValue('gray.600', 'gray.400');
  const toast = useToast();
  const {
    getOptions,
    isLoading: isLoadingCodes,
    getIncomeReplacementOtherCodes,
  } = useAnswerCodes();

  const wasConfigured =
    initialBenefits && Object.keys(initialBenefits).length > 0;

  const [isConsentLoading, setIsConsentLoading] = useState(false);
  const [benefits, setBenefits] = useState<AccidentBenefits>(() =>
    wasConfigured ? initialBenefits : createEmptyAccidentBenefits(),
  );
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  const {
    isOpen: isAttestationOpen,
    onOpen: onAttestationOpen,
    onClose: onAttestationClose,
  } = useDisclosure();

  const {
    isOpen: isCancelConfirmOpen,
    onOpen: onCancelConfirmOpen,
    onClose: onCancelConfirmClose,
  } = useDisclosure();

  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen) {
      setBenefits(
        wasConfigured ? initialBenefits : createEmptyAccidentBenefits(),
      );
      setTouchedFields(new Set());
    }
  }, [isOpen, wasConfigured, initialBenefits]);

  const handleChoiceLimitChange = useCallback(
    (
      enabledKey: AccidentBenefitEnabledKey,
      limitKey: AccidentBenefitLimitKey,
      value: string,
    ) => {
      setTouchedFields((prev) => new Set(prev).add(enabledKey));
      setBenefits((prev) => {
        const isDeclined = value === 'declined';
        const updates: Partial<AccidentBenefits> = {
          [enabledKey]: !isDeclined && value !== '',
          [limitKey]: isDeclined ? '' : value,
        };
        const incomeReplacementOtherCodes = getIncomeReplacementOtherCodes();
        if (
          limitKey === 'income_replacement_limit_per_week_cad' &&
          (isDeclined || !incomeReplacementOtherCodes.includes(value))
        ) {
          updates.income_replacement_limit_per_week_cad_other = null;
        }
        return { ...prev, ...updates };
      });
    },
    [getIncomeReplacementOtherCodes],
  );

  const handleIncomeReplacementOtherChange = useCallback(
    (valueString: string) => {
      const value = valueString === '' ? null : parseInt(valueString, 10);
      setBenefits((prev) => ({
        ...prev,
        income_replacement_limit_per_week_cad_other: isNaN(value as number)
          ? null
          : value,
      }));
    },
    [],
  );

  const handleBooleanSelectChange = useCallback(
    (key: AccidentBenefitEnabledKey, value: string) => {
      setTouchedFields((prev) => new Set(prev).add(key));
      setBenefits((prev) => ({
        ...prev,
        [key]: value === 'included',
      }));
    },
    [],
  );

  const handleOptionalNumberSelectChange = useCallback(
    (
      enabledKey: AccidentBenefitEnabledKey,
      limitKey: AccidentBenefitLimitKey,
      value: string,
    ) => {
      setTouchedFields((prev) => new Set(prev).add(enabledKey));
      const isIncluded = value === 'included';
      setBenefits((prev) => ({
        ...prev,
        [enabledKey]: isIncluded,
        [limitKey]: isIncluded ? prev[limitKey] : null,
      }));
    },
    [],
  );

  const handleOptionalLimitChange = useCallback(
    (limitKey: AccidentBenefitLimitKey, value: number | null) => {
      setBenefits((prev) => ({
        ...prev,
        [limitKey]: value,
      }));
    },
    [],
  );

  const getDisabledRecommendedBenefits = useCallback(() => {
    const disabled: AccidentBenefitDefinition[] = [];
    for (const def of ACCIDENT_BENEFIT_DEFINITIONS) {
      if (def.isRecommended && !benefits[def.enabledKey]) {
        disabled.push(def);
      }
    }
    return disabled;
  }, [benefits]);

  const handleApply = useCallback(() => {
    const disabledRecommended = getDisabledRecommendedBenefits();

    if (disabledRecommended.length > 0 && !initialConfirmed) {
      onAttestationOpen();
    } else {
      onSave(benefits, true);
      onClose();
    }
  }, [
    benefits,
    onSave,
    onClose,
    onAttestationOpen,
    getDisabledRecommendedBenefits,
    initialConfirmed,
  ]);

  const handleAttestationConfirm = useCallback(
    async (consentMethod: number) => {
      setIsConsentLoading(true);
      try {
        await apiCreateQuoteConsent(quoteId, {
          type: CONSENT_TYPE_ACCIDENT_BENEFITS_DECLINE,
          method: consentMethod,
          vehicle_vin: vehicleVin || undefined,
          vehicle_id: !vehicleVin ? vehicleId : undefined,
        });
        onAttestationClose();
        onSave(benefits, true);
        onClose();
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to record consent. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsConsentLoading(false);
      }
    },
    [
      quoteId,
      vehicleVin,
      vehicleId,
      benefits,
      onSave,
      onClose,
      onAttestationClose,
      toast,
    ],
  );

  const hasChanges = useMemo(() => {
    if (!wasConfigured) {
      return touchedFields.size > 0;
    }
    return JSON.stringify(benefits) !== JSON.stringify(initialBenefits);
  }, [benefits, initialBenefits, wasConfigured, touchedFields]);

  const handleCancelClick = useCallback(() => {
    if (hasChanges) {
      onCancelConfirmOpen();
    } else {
      onClose();
    }
  }, [hasChanges, onCancelConfirmOpen, onClose]);

  const handleConfirmDiscard = useCallback(() => {
    onCancelConfirmClose();
    onClose();
  }, [onCancelConfirmClose, onClose]);

  const disabledRecommended = getDisabledRecommendedBenefits();

  const getBooleanSelectValue = (
    enabledKey: AccidentBenefitEnabledKey,
  ): string => {
    const isEnabled = benefits[enabledKey];
    if (isEnabled === true) return 'included';
    if (initialConfirmed || touchedFields.has(enabledKey)) return 'declined';
    return '';
  };

  const getChoiceSelectValue = (
    enabledKey: AccidentBenefitEnabledKey,
    limitKey: AccidentBenefitLimitKey,
  ): string => {
    const limitValue = benefits[limitKey] as string;
    if (limitValue) return limitValue;
    const isEnabled = benefits[enabledKey];
    if (!isEnabled && (initialConfirmed || touchedFields.has(enabledKey))) {
      return 'declined';
    }
    return '';
  };

  const isAllConfigured = useMemo(() => {
    for (const def of ACCIDENT_BENEFIT_DEFINITIONS) {
      if (def.limitType === 'choice') {
        const selectValue = getChoiceSelectValue(def.enabledKey, def.limitKey!);
        if (!selectValue) return false;
      } else if (
        def.limitType === 'optional_number' ||
        def.limitType === 'none'
      ) {
        const selectValue = getBooleanSelectValue(def.enabledKey);
        if (!selectValue) return false;
        if (benefits[def.enabledKey] && def.coverageOptionsField) {
          if (!benefits[def.coverageOptionsField]?.length) return false;
        }
      }
    }
    return true;
  }, [benefits, touchedFields, initialConfirmed]);

  const renderChoiceBenefit = (def: AccidentBenefitDefinition) => {
    const limitOptions = def.answerCodeField
      ? getOptions(def.answerCodeField as AnswerCodeField).filter(
          (o) => o.value !== '',
        )
      : [];
    const selectValue = getChoiceSelectValue(def.enabledKey, def.limitKey!);
    const isIncomeReplacement =
      def.limitKey === 'income_replacement_limit_per_week_cad';
    const incomeReplacementOtherCodes = getIncomeReplacementOtherCodes();
    const showOtherInput =
      isIncomeReplacement &&
      selectValue !== 'declined' &&
      incomeReplacementOtherCodes.includes(selectValue);
    const otherValue = benefits.income_replacement_limit_per_week_cad_other;
    const isOtherMissing =
      showOtherInput && (otherValue === null || otherValue === undefined);
    const isNotConfigured = !selectValue;

    return (
      <Box key={def.enabledKey} py={3}>
        <HStack justify="space-between" align="flex-start" spacing={4}>
          <Box flex="1">
            <Text fontWeight="600" mb={2}>
              {def.name}
            </Text>
            <Text
              fontSize="sm"
              color={descriptionColor}
              pl={3}
              borderLeft="2px solid"
              borderColor="orange.400"
            >
              {def.description}
            </Text>
          </Box>
          <VStack spacing={2} align="flex-end">
            <Select
              variant="main"
              size="sm"
              w="180px"
              minW="180px"
              value={selectValue}
              onChange={(e) =>
                handleChoiceLimitChange(
                  def.enabledKey,
                  def.limitKey!,
                  e.target.value,
                )
              }
              isDisabled={isLoadingCodes}
              isInvalid={isNotConfigured}
              borderColor={isNotConfigured ? 'red.600' : undefined}
              placeholder="Select..."
            >
              <option value="declined">Declined</option>
              {limitOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
            {showOtherInput && (
              <NumberInput
                variant="main"
                size="sm"
                w="180px"
                min={1}
                value={otherValue ?? ''}
                onChange={handleIncomeReplacementOtherChange}
                isInvalid={isOtherMissing}
              >
                <NumberInputField
                  placeholder="Enter amount"
                  borderColor={isOtherMissing ? 'red.600' : undefined}
                />
              </NumberInput>
            )}
          </VStack>
        </HStack>
      </Box>
    );
  };

  const renderBooleanBenefit = (def: AccidentBenefitDefinition) => {
    const value = getBooleanSelectValue(def.enabledKey);

    return (
      <Box key={def.enabledKey} py={3}>
        <HStack justify="space-between" align="flex-start" spacing={4}>
          <Box flex="1">
            <Text fontWeight="600" mb={2}>
              {def.name}
            </Text>
            <Text
              fontSize="sm"
              color={descriptionColor}
              pl={3}
              borderLeft="2px solid"
              borderColor="orange.400"
            >
              {def.description}
            </Text>
          </Box>
          <Select
            variant="main"
            size="sm"
            w="180px"
            minW="180px"
            value={value}
            onChange={(e) =>
              handleBooleanSelectChange(def.enabledKey, e.target.value)
            }
            isInvalid={!value}
            borderColor={!value ? 'red.600' : undefined}
            placeholder="Select..."
          >
            <option value="included">Included</option>
            <option value="declined">Declined</option>
          </Select>
        </HStack>
      </Box>
    );
  };

  const renderOptionalNumberBenefit = (def: AccidentBenefitDefinition) => {
    const selectValue = getBooleanSelectValue(def.enabledKey);
    const isIncluded = benefits[def.enabledKey];
    const limitValue = benefits[def.limitKey!] as number | null;

    const coverageOptionsField = def.coverageOptionsField;
    const coverageOptions = coverageOptionsField
      ? benefits[coverageOptionsField] || []
      : [];
    const availableOptions = coverageOptionsField
      ? getOptions(coverageOptionsField).filter((o) => o.value !== '')
      : [];
    const isCoverageOptionsMissing =
      isIncluded && coverageOptionsField && coverageOptions.length === 0;

    const handleCoverageOptionsChange = (values: string[]) => {
      if (!coverageOptionsField) return;
      setBenefits((prev) => ({
        ...prev,
        [coverageOptionsField]: values,
      }));
    };

    const hasCoverageOptions =
      coverageOptionsField && availableOptions.length > 0;

    return (
      <Box key={def.enabledKey} py={3}>
        <HStack justify="space-between" align="flex-start" spacing={4}>
          <Box flex="1">
            <Text fontWeight="600" mb={2}>
              {def.name}
            </Text>
            <Text
              fontSize="sm"
              color={descriptionColor}
              pl={3}
              borderLeft="2px solid"
              borderColor="orange.400"
            >
              {def.description}
            </Text>
          </Box>
          {hasCoverageOptions ? (
            <VStack spacing={2} align="flex-end">
              <HStack spacing={2}>
                <Select
                  variant="main"
                  size="sm"
                  w="140px"
                  minW="140px"
                  value={selectValue}
                  onChange={(e) =>
                    handleOptionalNumberSelectChange(
                      def.enabledKey,
                      def.limitKey!,
                      e.target.value,
                    )
                  }
                  isInvalid={!selectValue}
                  borderColor={!selectValue ? 'red.600' : undefined}
                  placeholder="Select..."
                >
                  <option value="included">Included</option>
                  <option value="declined">Declined</option>
                </Select>
                {isIncluded && (
                  <NumberInput
                    variant="main"
                    size="sm"
                    w="130px"
                    min={0}
                    value={limitValue ?? ''}
                    onChange={(_, valueNumber) =>
                      handleOptionalLimitChange(
                        def.limitKey!,
                        isNaN(valueNumber) ? null : valueNumber,
                      )
                    }
                  >
                    <NumberInputField placeholder="BASE" />
                  </NumberInput>
                )}
              </HStack>
              {isIncluded && (
                <Box
                  borderWidth={isCoverageOptionsMissing ? '1px' : undefined}
                  borderColor={isCoverageOptionsMissing ? 'red.600' : undefined}
                  borderRadius="md"
                  p={isCoverageOptionsMissing ? 2 : 0}
                >
                  <Text fontSize="xs" color="gray.500" mb={1}>
                    Select at least one:
                  </Text>
                  <CheckboxGroup
                    value={coverageOptions}
                    onChange={handleCoverageOptionsChange}
                  >
                    <HStack spacing={4}>
                      {availableOptions.map((opt) => (
                        <Checkbox key={opt.value} value={opt.value} size="sm">
                          {opt.label}
                        </Checkbox>
                      ))}
                    </HStack>
                  </CheckboxGroup>
                </Box>
              )}
            </VStack>
          ) : (
            <HStack spacing={2}>
              <Select
                variant="main"
                size="sm"
                w="140px"
                minW="140px"
                value={selectValue}
                onChange={(e) =>
                  handleOptionalNumberSelectChange(
                    def.enabledKey,
                    def.limitKey!,
                    e.target.value,
                  )
                }
                isInvalid={!selectValue}
                borderColor={!selectValue ? 'red.600' : undefined}
                placeholder="Select..."
              >
                <option value="included">Included</option>
                <option value="declined">Declined</option>
              </Select>
              {isIncluded && (
                <NumberInput
                  variant="main"
                  size="sm"
                  w="130px"
                  min={0}
                  value={limitValue ?? ''}
                  onChange={(_, valueNumber) =>
                    handleOptionalLimitChange(
                      def.limitKey!,
                      isNaN(valueNumber) ? null : valueNumber,
                    )
                  }
                >
                  <NumberInputField placeholder="BASE" />
                </NumberInput>
              )}
            </HStack>
          )}
        </HStack>
      </Box>
    );
  };

  const choiceBenefits = ACCIDENT_BENEFIT_DEFINITIONS.filter(
    (d) => d.limitType === 'choice',
  );
  const optionalNumberBenefits = ACCIDENT_BENEFIT_DEFINITIONS.filter(
    (d) => d.limitType === 'optional_number',
  );
  const booleanBenefits = ACCIDENT_BENEFIT_DEFINITIONS.filter(
    (d) => d.limitType === 'none',
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="4xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader fontWeight="600" pb={2}>
            Accident Benefits
          </ModalHeader>
          <ModalCloseButton onClick={handleCancelClick} />

          <ModalBody>
            <VStack spacing={0} align="stretch" divider={<Divider />}>
              {/* Standard Benefits */}
              <Box py={3}>
                <Text fontWeight="600" mb={3} textDecoration="underline">
                  Standard Benefits
                </Text>
                <VStack spacing={0} align="stretch" divider={<Divider />}>
                  <Box py={3}>
                    <Text fontWeight="600" mb={2}>
                      Medical Benefit
                    </Text>
                    <Text
                      fontSize="sm"
                      color={descriptionColor}
                      pl={3}
                      borderLeft="2px solid"
                      borderColor="orange.400"
                    >
                      Covers reasonable and necessary medical expenses resulting
                      from an automobile accident that are not paid by OHIP or
                      another health plan, subject to policy limits.
                    </Text>
                  </Box>
                  <Box py={3}>
                    <Text fontWeight="600" mb={2}>
                      Rehabilitation Benefit
                    </Text>
                    <Text
                      fontSize="sm"
                      color={descriptionColor}
                      pl={3}
                      borderLeft="2px solid"
                      borderColor="orange.400"
                    >
                      Covers rehabilitation services required to assist in
                      recovery after an automobile accident, including approved
                      therapies intended to help restore function and
                      independence, subject to policy limits.
                    </Text>
                  </Box>
                  <Box py={3}>
                    <Text fontWeight="600" mb={2}>
                      Attendant Care Benefit
                    </Text>
                    <Text
                      fontSize="sm"
                      color={descriptionColor}
                      pl={3}
                      borderLeft="2px solid"
                      borderColor="orange.400"
                    >
                      Covers the cost of personal care services if injuries from
                      an automobile accident prevent you from performing basic
                      daily activities, subject to policy limits.
                    </Text>
                  </Box>
                </VStack>
              </Box>

              {/* Optional Benefits */}
              <Box py={3}>
                <Text fontWeight="600" mb={3} textDecoration="underline">
                  Optional Benefits
                </Text>
                <VStack spacing={0} align="stretch" divider={<Divider />}>
                  {choiceBenefits.map((def) => renderChoiceBenefit(def))}
                  {optionalNumberBenefits.map((def) =>
                    renderOptionalNumberBenefit(def),
                  )}
                  {booleanBenefits.map((def) => renderBooleanBenefit(def))}
                </VStack>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter gap={2} pt={4}>
            <Button variant="outline" onClick={handleCancelClick}>
              Cancel
            </Button>
            <Button
              colorScheme="yellow"
              onClick={handleApply}
              isDisabled={!isAllConfigured}
            >
              Apply
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <DeclineAttestationModal
        isOpen={isAttestationOpen}
        onClose={onAttestationClose}
        onConfirm={handleAttestationConfirm}
        declinedItems={disabledRecommended.map((def) => ({
          key: def.enabledKey,
          name: def.name,
        }))}
        attestationText={COVERAGE_ATTESTATION_TEXT}
        isLoading={isConsentLoading}
      />

      <AlertDialog
        isOpen={isCancelConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={onCancelConfirmClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Discard Changes?
            </AlertDialogHeader>

            <AlertDialogBody>
              Your changes have not been saved. Are you sure you want to discard
              your changes?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCancelConfirmClose}>
                No, go back
              </Button>
              <Button colorScheme="red" onClick={handleConfirmDiscard} ml={3}>
                Yes, discard changes
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default AccidentBenefitsModal;
